/**
 * bpmnShared - EINE gemeinsame Basis fuer alle iFlow-/BPMN-Visualisierungen.
 *
 * Enthaelt: BPMN-Parser (DI-Koordinaten, deterministisch - gleiche XML ergibt exakt
 * dieselbe Geometrie), Kanten-Clipping, Icon-/Typ-Zuordnung, Kategorien-Akzente und
 * den statischen SVG-String-Renderer in der Bildsprache des KI iFlow Builders
 * (neutrale Karten, monochrome Icons, Typ-Label in Micro-Caps, 3px-Akzentkante).
 *
 * Farb-Konzept: Die interaktiven Renderer (IflowDiagram) sind token-getrieben und folgen
 * Light/Dark automatisch. Der statische Doku-Renderer (renderIflowSvgString) nutzt dagegen
 * eine FESTE helle Palette (Light-Token-Werte als Hex): eine Dokumentation ist ein
 * Papier-Artefakt und bleibt IMMER hell - in der App (auch im Dark Mode), im Editor
 * und im Print-PDF identisch.
 */

export function parseBpmnElements(xmlStr) {
  try {
    const doc = new DOMParser().parseFromString(xmlStr, "application/xml");
    const res = {};
    const tags = ["serviceTask", "scriptTask", "callActivity", "task", "receiveTask", "sendTask",
      "exclusiveGateway", "inclusiveGateway", "parallelGateway", "eventBasedGateway",
      "startEvent", "endEvent", "intermediateCatchEvent", "intermediateThrowEvent", "subProcess", "participant", "messageFlow"];
    for (const tag of tags) {
      for (const el of doc.getElementsByTagNameNS("*", tag)) {
        const id = el.getAttribute("id"); if (!id) continue;
        const name = el.getAttribute("name") || "";
        const properties = {};
        for (const pe of el.getElementsByTagNameNS("*", "property")) {
          const keyEl = pe.getElementsByTagNameNS("*", "key")[0];
          const valEl = pe.getElementsByTagNameNS("*", "value")[0];
          const k = keyEl ? keyEl.textContent.trim() : (pe.getAttribute("key") || "");
          const v2 = valEl ? valEl.textContent.trim() : (pe.getAttribute("value") || "");
          if (k) properties[k] = v2;
        }
        const cmdUri = properties.cmdVariantUri || "";
        const cname = (cmdUri.match(/cname::([^/]+)/) || [])[1] || "";
        res[id] = { name, type: el.localName || tag, properties, activityType: properties.activityType || "", subActivityType: properties.subActivityType || "", cname };
      }
    }
    return res;
  } catch { return {}; }
}

export function parseBpmnLayout(xmlStr) {
  try {
    const doc = new DOMParser().parseFromString(xmlStr, "application/xml");
    const shapes = {}, edges = [], shapeIdToElementId = {};
    for (const s of doc.getElementsByTagNameNS("*", "BPMNShape")) {
      const elId = s.getAttribute("bpmnElement"), shapeId = s.getAttribute("id");
      if (elId && shapeId) shapeIdToElementId[shapeId] = elId;
      if (!elId) continue;
      const b = s.getElementsByTagNameNS("*", "Bounds")[0]; if (!b) continue;
      shapes[elId] = { x: +b.getAttribute("x") || 0, y: +b.getAttribute("y") || 0, w: +b.getAttribute("width") || 100, h: +b.getAttribute("height") || 80 };
    }
    const mfIds = new Set();
    for (const mf of doc.getElementsByTagNameNS("*", "messageFlow")) if (mf.getAttribute("id")) mfIds.add(mf.getAttribute("id"));
    for (const e of doc.getElementsByTagNameNS("*", "BPMNEdge")) {
      const elId = e.getAttribute("bpmnElement"); if (!elId) continue;
      const src = shapeIdToElementId[e.getAttribute("sourceElement") || ""] || "";
      const tgt = shapeIdToElementId[e.getAttribute("targetElement") || ""] || "";
      const wps = [];
      for (const wp of e.getElementsByTagNameNS("*", "waypoint")) wps.push({ x: +wp.getAttribute("x") || 0, y: +wp.getAttribute("y") || 0 });
      if (wps.length >= 2) edges.push({ id: elId, sourceElementId: src, targetElementId: tgt, waypoints: wps, isMessageFlow: mfIds.has(elId) });
    }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const s of Object.values(shapes)) { minX = Math.min(minX, s.x); minY = Math.min(minY, s.y); maxX = Math.max(maxX, s.x + s.w); maxY = Math.max(maxY, s.y + s.h); }
    if (!isFinite(minX)) { minX = 0; minY = 0; maxX = 800; maxY = 400; }
    return { shapes, edges, bbox: { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY } };
  } catch { return { shapes: {}, edges: [], bbox: { minX: 0, minY: 0, maxX: 800, maxY: 400, w: 800, h: 400 } }; }
}

// Kanten am Shape-Rand beginnen/enden lassen (Rechteck-/Kreis-Schnitt statt Mittelpunkt)
export function clipEdge(edge, shapes, elements) {
  if (!edge.waypoints || edge.waypoints.length < 2) return edge.waypoints || [];
  const pts = edge.waypoints.map(wp => ({ ...wp }));
  const sS = shapes[edge.sourceElementId], tS = shapes[edge.targetElementId];
  const circle = id => { const t = elements[id]?.type || ""; return t.includes("Event") || t.includes("event"); };
  const cR = (sh, dx, dy) => { const cx = sh.x + sh.w / 2, cy = sh.y + sh.h / 2; if (Math.abs(dx) < .001 && Math.abs(dy) < .001) return { x: cx + sh.w / 2, y: cy }; const t = Math.min(Math.abs(dx) > .001 ? (sh.w / 2) / Math.abs(dx) : Infinity, Math.abs(dy) > .001 ? (sh.h / 2) / Math.abs(dy) : Infinity); return { x: cx + dx * t, y: cy + dy * t }; };
  const cC = (sh, dx, dy) => { const cx = sh.x + sh.w / 2, cy = sh.y + sh.h / 2, r = Math.min(sh.w, sh.h) / 2, len = Math.hypot(dx, dy); if (len < .001) return { x: cx + r, y: cy }; return { x: cx + dx / len * r, y: cy + dy / len * r }; };
  if (sS) { const dx = pts[1].x - (sS.x + sS.w / 2), dy = pts[1].y - (sS.y + sS.h / 2); pts[0] = circle(edge.sourceElementId) ? cC(sS, dx, dy) : cR(sS, dx, dy); }
  if (tS) { const n = pts.length, dx = pts[n - 2].x - (tS.x + tS.w / 2), dy = pts[n - 2].y - (tS.y + tS.h / 2); pts[n - 1] = circle(edge.targetElementId) ? cC(tS, dx, dy) : cR(tS, dx, dy); }
  return pts;
}

// activityType -> kompaktes Icon (SVG-Pfade, stroke=currentColor bzw. explizite Farbe)
export const NODE_ICON = {
  Enricher: '<path d="M4 6h16M4 12h10M4 18h7"/>',
  Script: '<path d="M8 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/><path d="M14 4h6v6"/>',
  ExternalCall: '<path d="M4 7h13l-3-3M20 17H7l3 3"/>',
  RequestReply: '<path d="M4 7h13l-3-3M20 17H7l3 3"/>',
  Send: '<path d="M4 7h13l-3-3M20 17H7l3 3"/>',
  Mapping: '<path d="M7 5v14M17 5v14"/><path d="M7 9h5M7 13h6" opacity=".6"/>',
  XsltMapping: '<path d="M7 5v14M17 5v14"/><path d="M7 9h5M7 13h6" opacity=".6"/>',
  Splitter: '<path d="M4 12h6M14 6h6M14 18h6"/><path d="M10 12l4-6M10 12l4 6" opacity=".6"/>',
  Gather: '<path d="M4 6h6M4 18h6M14 12h6"/><path d="M10 6l4 6M10 18l4-6" opacity=".6"/>',
  Aggregator: '<path d="M4 6h6M4 18h6M14 12h6"/>',
  ProcessCallElement: '<path d="M12 2 2 7l10 5 10-5z"/><path d="M2 17l10 5 10-5"/>',
  IdempotentProcessCall: '<path d="M12 2 2 7l10 5 10-5z"/><path d="M2 17l10 5 10-5"/>',
  DataStore: '<ellipse cx="12" cy="6" rx="8" ry="3"/><path d="M4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6"/>',
  Filter: '<path d="M3 5h18l-7 8v6l-4-2v-4z"/>',
};
export const iconFor = (at) => NODE_ICON[at] || '<path d="M4 6h16M4 12h10M4 18h7"/>';
export const TYPE_LABEL = { Enricher: "Content Modifier", Script: "Script", ExternalCall: "Request Reply", RequestReply: "Request Reply", Send: "Send", Mapping: "Mapping", XsltMapping: "XSLT", Splitter: "Splitter", Gather: "Gather", Aggregator: "Aggregator", ProcessCallElement: "Process Call", IdempotentProcessCall: "Idempotent Call", DataStore: "Data Store", Filter: "Filter", WriteVariable: "Variables" };

// Vier Kategorien-Akzente (statt zehn Fuellfarben): Transform, Route, Call, Persist.
// Print-Fallback bewusst monochrom - auf Papier tragen Icon + Typ-Label die Semantik.
const CAT_OF = {
  Enricher: "transform", Script: "transform", Mapping: "transform", XsltMapping: "transform",
  Splitter: "route", Gather: "route", Aggregator: "route", Filter: "route",
  ExternalCall: "call", RequestReply: "call", Send: "call", ProcessCallElement: "call", IdempotentProcessCall: "call", ProcessCall: "call",
  DataStore: "persist", WriteVariable: "persist",
};
const CAT_COLOR = {
  transform: "var(--iio-info,#3C3C3C)",
  route: "var(--iio-warning,#3C3C3C)",
  call: "var(--iio-signal,#3C3C3C)",
  persist: "var(--iio-text-3,#3C3C3C)",
};
export function categoryOf(info) {
  const at = info?.activityType || info?.cname || "";
  const key = CAT_OF[at] || null;
  return key ? { key, color: CAT_COLOR[key] } : null;
}

// Einheitliche Label-Kuerzung: mode "middle" (Karten - vorne+hinten sind semantisch),
// sonst hinten mit echtem Ellipsis-Zeichen.
export function truncateLabel(text, max, mode = "end") {
  const t = String(text || "");
  if (t.length <= max) return t;
  if (mode === "middle") {
    const front = Math.ceil((max - 1) / 2), back = Math.floor((max - 1) / 2);
    return t.slice(0, front) + "…" + t.slice(t.length - back);
  }
  return t.slice(0, max - 1) + "…";
}

// Feste helle Palette fuer die Doku (Light-Token-Werte aus iio-tokens.css als Hex).
// Bewusst KEINE var()-Referenzen: das Dokument bleibt IMMER hell (siehe Kopf-Kommentar).
const C = {
  paper: "#F5F2EC",
  partyFill: "#FAF8F3",
  cardFill: "#FFFFFF",
  // Gleicher Wert wie --iio-dia-node-border (Light): der alte #DDD5C5 erreichte
  // nur 1.31:1 gegen das Papier, die Kartenkontur verschwand beim Verkleinern.
  cardBorder: "#A89C84",
  strip: "#EFEAE0",
  t1: "#1A2A30",
  t2: "#4A5862",
  t3: "#7A8590",
  t4: "#A8AEB6",
  line: "#6E7A85",
  signal: "#E85D2E",
  ok: "#2D8A5A",
  crit: "#C8362E",
};
// Kategorien-Akzente in der Doku: fixe Light-Werte (App-Pendants: info/warning/signal/text-3)
const DOC_CAT = { transform: "#2D6FB8", route: "#C77A0B", call: "#E85D2E", persist: "#7A8590" };
const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// Woerter auf Zeilen verteilen (fuer voll ausgeschriebene Namen statt Kuerzung).
// Ein einzelnes Ueberlaenge-Wort bleibt in einer Zeile (leichter Ueberlauf statt Abschneiden).
function wrapWords(text, maxChars) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = []; let cur = "";
  for (const w of words) {
    if (!cur) { cur = w; continue; }
    if ((cur + " " + w).length <= maxChars) cur += " " + w;
    else { lines.push(cur); cur = w; }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [""];
}

/**
 * Beschriftungs-Layout einer Task-Karte - EINE Quelle fuer Doku-String-Renderer
 * und interaktiven Canvas (IflowDiagram). Namen werden voll ausgeschrieben:
 * Umbruch, dann Schrift verkleinern (11 -> 8.5), max. 3 Zeilen; das Typ-Label
 * schrumpft bis es passt. Nie abschneiden.
 * Rueckgabe in Nutzerkoordinaten des Shapes.
 */
export function layoutNodeLabel(name, typeText, sh, opts = {}) {
  // opts.eff/opts.minPx (nur interaktiver Canvas): hebt die Schrift bei starkem
  // Herauszoomen an, damit die Beschriftung SICHTBAR bleibt statt auf 3px zu
  // schrumpfen - begrenzt durch Boxbreite und -hoehe, also nie ueberlaufend.
  // Ohne opts ist das Ergebnis unveraendert (Doku-/Print-Renderer).
  const { eff = 0, minPx = 0 } = opts;
  const cx = sh.x + sh.w / 2, cy = sh.y + sh.h / 2;
  const avail = sh.w - 18;
  const wrapAt = f => wrapWords(name, Math.max(6, Math.floor(avail / (f * 0.54))));
  let fs = 11, lines = wrapAt(fs);
  while (lines.length > 2 && fs > 8.5) { fs -= 0.5; lines = wrapAt(fs); }
  if (lines.length > 3) lines = lines.slice(0, 2).concat(lines.slice(2).join(" "));
  let tfs = 8.5, tls = 0.6;
  const tt = String(typeText || "").toUpperCase();
  if (minPx > 0 && eff > 0 && fs * eff < minPx) {
    // Zielgroesse gegen den Zoom, dann so weit verkleinern bis Zeilen und Hoehe passen
    const budget = sh.h - 22;
    let f = Math.min(26, minPx / eff), ls = name ? wrapAt(f) : [""];
    while (f > fs && (ls.length > 3 || ls.length * (f + 2.5) > budget)) { f -= 0.5; ls = wrapAt(f); }
    if (f > fs && ls.length <= 3 && ls.length * (f + 2.5) <= budget) { fs = f; lines = ls; }
    // Typ-Zeile mitziehen, aber nur solange sie in die Breite passt
    let t = Math.min(12, Math.max(tfs, minPx / eff));
    while (t > tfs && tt.length * t * 0.62 > avail) t -= 0.3;
    tfs = t;
  }
  const lh = fs + 2.5;
  const y0 = cy - 3 - (lines.length - 1) * (lh / 2 + 1.5);
  while (tt.length * tfs * 0.62 + (tt.length - 1) * tls > avail && tfs > 6.6) { tfs -= 0.3; tls = 0.4; }
  return {
    cx, cy, lines, fs, lh, y0,
    typeText: tt, tfs, tls,
    typeY: name ? y0 + (lines.length - 1) * lh + lh / 2 + 8 : cy + 13,
    showIcon: sh.h >= 44 && sh.w >= 72,
  };
}

/**
 * Statischer SVG-String in der Einheits-Bildsprache (nicht interaktiv).
 * Deterministisch: Layout 1:1 aus den DI-Koordinaten, festes viewBox, keine Zufaelle.
 * Genutzt von der Doku-Seite (Anzeige, Print-PDF); funktioniert dank Fallbacks auch
 * ausserhalb des Token-Scopes.
 */
export function renderIflowSvgString(xmlStr) {
  const layout = parseBpmnLayout(xmlStr);
  const elements = parseBpmnElements(xmlStr);
  const { shapes, edges, bbox } = layout;
  if (!Object.keys(shapes).length) return "";

  const isGateway = t => t && /gateway/i.test(t);
  const isEvent = t => t && /event/i.test(t);
  const poolIds = [], subIds = [], normalIds = [];
  for (const id of Object.keys(shapes)) {
    const t = elements[id]?.type || "";
    if (t === "participant") poolIds.push(id); else if (t === "subProcess") subIds.push(id); else normalIds.push(id);
  }

  const pad = 40;
  const font = 'font-family="Source Sans 3,Segoe UI,sans-serif"';
  let svg = `<svg viewBox="${bbox.minX - pad} ${bbox.minY - pad} ${bbox.w + pad * 2} ${bbox.h + pad * 2}" width="100%" xmlns="http://www.w3.org/2000/svg" ${font}>`;
  svg += `<defs><marker id="uifArrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse"><path d="M0 0 L10 5 L0 10 z" fill="${C.line}"/></marker>`;
  svg += `<marker id="uifArrowMsg" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0 L7 4 L0 8" fill="none" stroke="${C.signal}" stroke-width="1.4"/></marker></defs>`;
  // Papier-Grund: haelt das Dokument auch im Dark Mode der App und im Editor immer hell
  svg += `<rect x="${bbox.minX - pad}" y="${bbox.minY - pad}" width="${bbox.w + pad * 2}" height="${bbox.h + pad * 2}" rx="10" fill="${C.paper}"/>`;

  const renderShape = (id) => {
    const sh = shapes[id]; if (!sh) return "";
    const info = elements[id] || {}; const t = info.type || "task"; const name = info.name || "";

    if (isGateway(t)) {
      const cx = sh.x + sh.w / 2, cy = sh.y + sh.h / 2, r = Math.min(sh.w, sh.h) / 2, g = r * 0.4;
      let sym;
      if (t === "parallelGateway") sym = `<line x1="${cx}" y1="${cy - g}" x2="${cx}" y2="${cy + g}" stroke="${C.signal}" stroke-width="2" stroke-linecap="round"/><line x1="${cx - g}" y1="${cy}" x2="${cx + g}" y2="${cy}" stroke="${C.signal}" stroke-width="2" stroke-linecap="round"/>`;
      else sym = `<line x1="${cx - g * .7}" y1="${cy - g * .7}" x2="${cx + g * .7}" y2="${cy + g * .7}" stroke="${C.signal}" stroke-width="2" stroke-linecap="round"/><line x1="${cx + g * .7}" y1="${cy - g * .7}" x2="${cx - g * .7}" y2="${cy + g * .7}" stroke="${C.signal}" stroke-width="2" stroke-linecap="round"/>`;
      return `<polygon points="${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}" fill="${C.cardFill}" stroke="${C.signal}" stroke-width="1.3"/>${sym}`;
    }

    if (isEvent(t)) {
      const cx = sh.x + sh.w / 2, cy = sh.y + sh.h / 2, r = Math.min(sh.w, sh.h) / 2;
      const cmd = (info.properties?.cmdVariantUri || "").toLowerCase(), nm = name.toLowerCase();
      const isErr = cmd.includes("error") || nm.includes("error"), isTmr = cmd.includes("timer") || nm.includes("timer");
      const isEnd = t === "endEvent";
      const col = isErr || isEnd ? C.crit : C.ok;
      const icon = isTmr ? '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>' : isErr ? '<path d="M10 4 2 19h20L14 4z"/><path d="M12 10v4"/>' : '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>';
      let out = `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${C.cardFill}" stroke="${col}" stroke-width="${isEnd ? 2.6 : 1.6}"/>`;
      out += `<g stroke="${col}" fill="none" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" transform="translate(${cx - r * .5},${cy - r * .5}) scale(${r / 24})">${icon}</g>`;
      if (name) {
        // Voll ausgeschrieben; lange Namen brechen mittig in zwei Zeilen um
        let evLines = name.length > 20 ? wrapWords(name, Math.ceil(name.length / 2) + 2) : [name];
        if (evLines.length > 2) evLines = [evLines[0], evLines.slice(1).join(" ")];
        evLines.forEach((ln, i) => { out += `<text x="${cx}" y="${cy + r + 12 + i * 11}" text-anchor="middle" font-size="9.5" font-weight="500" fill="${C.t3}">${esc(ln)}</text>`; });
      }
      return out;
    }

    if (t === "participant") {
      const iflType = info.properties?.["ifl:type"] || "";
      // Prozess-Pools (Integration Process, Local/Error-Prozesse) vs. externe Parties (Sender/Receiver)
      const isProc = name === "Integration Process" || iflType === "IntegrationProcess" || sh.w >= 300 || sh.h >= 170;
      if (isProc) {
        // Builder-Look: solide Karte + 32px-Titelstreifen links mit vertikalem Micro-Caps-Label
        const r = 8, sw = 32;
        let out = `<rect x="${sh.x}" y="${sh.y}" width="${sh.w}" height="${sh.h}" rx="${r}" fill="${C.cardFill}" stroke="${C.cardBorder}" stroke-width="1.5"/>`;
        out += `<path d="M ${sh.x + r} ${sh.y} L ${sh.x + sw} ${sh.y} L ${sh.x + sw} ${sh.y + sh.h} L ${sh.x + r} ${sh.y + sh.h} Q ${sh.x} ${sh.y + sh.h} ${sh.x} ${sh.y + sh.h - r} L ${sh.x} ${sh.y + r} Q ${sh.x} ${sh.y} ${sh.x + r} ${sh.y} Z" fill="${C.strip}"/>`;
        out += `<line x1="${sh.x + sw}" y1="${sh.y + 1}" x2="${sh.x + sw}" y2="${sh.y + sh.h - 1}" stroke="${C.cardBorder}" stroke-width="1"/>`;
        out += `<rect x="${sh.x}" y="${sh.y}" width="${sh.w}" height="${sh.h}" rx="${r}" fill="none" stroke="${C.cardBorder}" stroke-width="1.5"/>`;
        if (name) out += `<text x="${sh.x + sw / 2 + 1}" y="${sh.y + sh.h / 2}" text-anchor="middle" dominant-baseline="middle" font-size="11" font-weight="700" letter-spacing="1.1" fill="${C.t2}" transform="rotate(-90 ${sh.x + sw / 2 + 1} ${sh.y + sh.h / 2})">${esc(name.toUpperCase())}</text>`;
        return out;
      }
      // Externe Party (Sender/Empfaenger): ruhige Karte mit Rolle + Name wie im KI-Builder
      const role = /sender/i.test(iflType) || /^sender/i.test(name) ? "SENDER" : "RECEIVER";
      const cx = sh.x + sh.w / 2, cy = sh.y + sh.h / 2;
      let fs = 12;
      while (name.length * fs * 0.54 > sh.w - 12 && fs > 7.5) fs -= 0.5;
      let out = `<rect x="${sh.x}" y="${sh.y}" width="${sh.w}" height="${sh.h}" rx="6" fill="${C.partyFill}" stroke="${C.cardBorder}" stroke-width="1.2"/>`;
      out += `<text x="${cx}" y="${cy - 8}" text-anchor="middle" dominant-baseline="middle" font-size="9.5" font-weight="600" letter-spacing="1" fill="${C.t3}">${role}</text>`;
      if (name) out += `<text x="${cx}" y="${cy + 7}" text-anchor="middle" dominant-baseline="middle" font-size="${fs}" font-weight="600" fill="${C.t1}">${esc(name)}</text>`;
      return out;
    }

    if (t === "subProcess") {
      // Builder-Look (aib-procblock): solide Karte + Icon-Kopfzeile in Micro-Caps
      const isExc = /exception|error/i.test(name) || /exception|error/i.test(info.activityType || "") || /exception|error/i.test(info.properties?.cmdVariantUri || "");
      const col = isExc ? C.crit : C.t3;
      const headIcon = isExc ? '<path d="M10 4 2 19h20L14 4z"/><path d="M12 10v4"/>' : '<path d="M12 2 2 7l10 5 10-5z"/><path d="M2 17l10 5 10-5"/>';
      let out = `<rect x="${sh.x}" y="${sh.y}" width="${sh.w}" height="${sh.h}" rx="8" fill="${C.cardFill}" stroke="${isExc ? C.crit : C.cardBorder}" stroke-width="1.4"/>`;
      out += `<g stroke="${col}" fill="none" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" transform="translate(${sh.x + 11},${sh.y + 8}) scale(0.5)">${headIcon}</g>`;
      if (name) out += `<text x="${sh.x + 27}" y="${sh.y + 15}" font-size="9.5" font-weight="700" letter-spacing="0.9" fill="${col}">${esc(name.toUpperCase())}</text>`;
      return out;
    }

    // Task-Karte: neutrale Flaeche + 3px-Akzentkante + Icon + Name + Typ-Label (Micro-Caps).
    // Namen und Typ-Labels werden voll ausgeschrieben: Namen brechen um (max. 3 Zeilen),
    // das Typ-Label verkleinert sich bis es passt - nie abschneiden.
    const at = info.activityType || "";
    const cat = categoryOf(info);
    const accent = cat ? DOC_CAT[cat.key] : C.t3;
    const cx = sh.x + sh.w / 2;
    let out = `<rect x="${sh.x}" y="${sh.y}" width="${sh.w}" height="${sh.h}" rx="8" fill="${C.cardFill}" stroke="${C.cardBorder}" stroke-width="1.2"/>`;
    if (cat) out += `<line x1="${sh.x + 2.5}" y1="${sh.y + 7}" x2="${sh.x + 2.5}" y2="${sh.y + sh.h - 7}" stroke="${accent}" stroke-width="3" stroke-linecap="round"/>`;
    const L = layoutNodeLabel(name, TYPE_LABEL[at] || at || t, sh);
    if (L.showIcon) out += `<g stroke="${accent}" fill="none" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" transform="translate(${sh.x + 8},${sh.y + 7}) scale(0.46)">${iconFor(at)}</g>`;
    if (name) L.lines.forEach((ln, i) => { out += `<text x="${cx}" y="${L.y0 + i * L.lh}" text-anchor="middle" dominant-baseline="middle" font-size="${L.fs}" font-weight="600" fill="${C.t1}">${esc(ln)}</text>`; });
    out += `<text x="${cx}" y="${L.typeY}" text-anchor="middle" dominant-baseline="middle" font-size="${L.tfs}" font-weight="600" letter-spacing="${L.tls}" fill="${C.t3}">${esc(L.typeText)}</text>`;
    return out;
  };

  for (const id of poolIds) svg += renderShape(id);
  for (const id of subIds) svg += renderShape(id);
  for (const e of edges) {
    const pts = clipEdge(e, shapes, elements).map(w => `${w.x},${w.y}`).join(" ");
    if (e.isMessageFlow) svg += `<polyline points="${pts}" fill="none" stroke="${C.signal}" stroke-width="1.1" stroke-dasharray="5,4" marker-end="url(#uifArrowMsg)"/>`;
    else svg += `<polyline points="${pts}" fill="none" stroke="${C.line}" stroke-width="1.4" marker-end="url(#uifArrow)"/>`;
  }
  for (const id of normalIds) svg += renderShape(id);

  svg += "</svg>";
  return svg;
}
